import { GlyphChange, GlyphState, IdeaBox, IdeaBoxEntry, TaskType } from "konect-model";
import domtoimage from 'dom-to-image';
import { GlyphEvent, GlyphEventService } from "./event-service";
import { nanoid } from "nanoid";
import { cloneDeep } from "lodash";
import { lerpColor } from "./util";

var mx = require("mxgraph")({
    mxImageBasePath: "mxgraph/javascript/src/images",
    mxBasePath: "mxgraph/javascript/src"
})

export interface InterpolationPoint {
    position: number,
    on: boolean
}

export class InterpolationPoints {
    points: InterpolationPoint[] = [];

    constructor(points: InterpolationPoint[]) {
        this.points = points;
    }

    getFactorAtPosition(position: number) {
        let p1;
        let p2;
        for (let i = 0; i < this.points.length - 1; i++) {
            const o1 = this.points[i];
            const o2 = this.points[i + 1];
            if (o1.position > position) {
                return 0;
            }
            if (o1.position <= position && o2.position >= position) {
                p1 = o1;
                p2 = o2;
                break;
            }
        }

        if (p1 && p2) {
            if (!p1.on && !p2.on) {
                return 0
            }
            if (p1.on && p2.on) {
                return 1
            }
            if (p1.on && !p2.on && p1.position !== p2.position) {
                return 1 - ((position - p1.position) / (p2.position - p1.position));
            }
            if (!p1.on && p2.on && p1.position !== p2.position) {
                return (position - p1.position) / (p2.position - p1.position)
            }
            if (p1.on && !p2.on && p1.position === p2.position) {
                return 0;
            }
            if (!p1.on && p2.on && p1.position === p2.position) {
                return 1;
            }
        }
        return 0;
    }
}

export class InterpolationEntry {
    entry: IdeaBoxEntry;
    states: GlyphState[];
    statePoints: { [key: string]: InterpolationPoints }
    factorMap: any = {};

    constructor(entry: IdeaBoxEntry, relevantStates: GlyphState[], statePoints: { [key: string]: InterpolationPoints }) {
        this.entry = entry;
        this.states = relevantStates;
        this.statePoints = statePoints;
    }

    getChangeAtPosition(cellId: string, position: number) {
        let change1;
        let change2;
        let base;

        this.factorMap = {};
        for (const state of this.states) {
            if (state.id === "default") {
                continue;
            }
            const points = this.statePoints[state.id];
            if (!points) return;
            const factor = points.getFactorAtPosition(position);
            for (const c of state.changes) {
                if (c.cellId !== cellId) continue;
                if (c.style) {
                    for (const key of Object.keys(c.style)) {
                        if (!this.factorMap[key]) {
                            this.factorMap[key] = 0;
                        }
                        this.factorMap[key] += factor;
                    }
                }
                if (c.geometry) {
                    for (const key of Object.keys(c.geometry)) {
                        if (c.geometry[key] === undefined) continue;
                        if (!this.factorMap[key]) {
                            this.factorMap[key] = 0;
                        }
                        this.factorMap[key] += factor;
                    }
                }
            }
        }

        for (const state of this.states) {
            const change = state.changes.find(c => c.cellId === cellId)
            if (!change) continue
            if (state.id === "default") {
                base = change;
                continue;
            }
            if (!base) continue;
            const points = this.statePoints[state.id];
            if (!points) return;
            const factor = points.getFactorAtPosition(position);
            change2 = cloneDeep(change);
            if (factor > 0) {
                change2 = this.interpolateGlyphChange(base, change1, change2, factor);
                change1 = change2;
            }
        }

        return change1;
    }

    getStateAtPosition(position: number) {
        const newState = new GlyphState(this.entry.id, "");

        const changes = []
        for (const cellId of this.entry.linkedCells) {
            const change = this.getChangeAtPosition(cellId, position)
            change && changes.push(change);
        }
        newState.changes = changes;

        return newState;
    }

    // ratio describes how much both changes influence the result, with 0 meaning change2 would not have an impact on the result
    public interpolateGlyphChange(defaultChange: GlyphChange, change1: GlyphChange | undefined, change2: GlyphChange | undefined, factor: number) {
        let change = new GlyphChange(defaultChange.cellId);
        if ((change1 && change1.geometry) || (change2 && change2.geometry)) {
            const g1 = change1 && change1.geometry ? change1.geometry : {};
            const g2 = change2 && change2.geometry ? change2.geometry : {};
            const merged = Object.assign(cloneDeep(g1), cloneDeep(g2));
            for (const key of Object.keys(merged)) {
                const actualFactor = factor / this.factorMap[key];
                if (g1[key] !== undefined && g2[key] !== undefined) {
                    if (typeof g1[key] === 'number') {
                        merged[key] = g1[key] + (g2[key] - g1[key]) * actualFactor
                    }
                } else if (g1[key] !== undefined && factor === this.factorMap[key]) { // if the change is the only change currently active, interpolate with default
                    if (typeof g1[key] === 'number') {
                        merged[key] = g1[key] * factor
                    }
                } else if (g2[key] !== undefined && factor === this.factorMap[key]) { // if the change is the only change currently active, interpolate with default
                    if (typeof g2[key] === 'number') {
                        merged[key] = g2[key] * factor
                    }
                } else if (g1[key] !== undefined) {
                    merged[key] = g1[key];
                } else if (g2[key] !== undefined) {
                    merged[key] = g2[key];
                }
            }
            change.geometry = merged;
        }

        if ((change1 && change1.style) || (change2 && change2.style)) {
            const s1 = change1 && change1.style ? change1.style : {};
            const s2 = change2 && change2.style ? change2.style : {};
            const d = defaultChange.style;
            const merged = Object.assign(cloneDeep(s1), cloneDeep(s2));
            for (const key of Object.keys(merged)) {
                const actualFactor = factor / this.factorMap[key];
                if (s1[key] !== undefined && s2[key] !== undefined) {
                    if (s1[key].includes("#")) {
                        merged[key] = lerpColor(s1[key], s2[key], actualFactor);
                    }
                } else if (s1[key] !== undefined && factor === this.factorMap[key]) {
                    if (s1[key].includes("#")) {
                        merged[key] = lerpColor(d[key] || "#ffffff", s1[key], factor);
                    }
                } else if (s2[key] !== undefined && factor === this.factorMap[key]) {
                    if (s2[key].includes("#")) {
                        merged[key] = lerpColor(d[key] || "#ffffff", s2[key], factor);
                    }
                } else if (s1[key] !== undefined) {
                    merged[key] = s1[key];
                } else if (s2[key] !== undefined) {
                    merged[key] = s2[key];
                }
            }
            change.style = merged;
        }
        return change
    }
}

export class MXGraphService {

    // Converts an mxGraph style string to an object
    public static styleStringToObject(style: string) {
        const entries = style.split(";");
        const s: { [key: string]: string } = {};
        for (const e of entries) {
            const parts = e.split("=")
            const isBool = parts.length === 1;
            if (!isBool && parts.length === 2) {
                s[parts[0]] = parts[1];
            } else if (parts[0]) {
                s[parts[0]] = "bool";
            }
        }
        return s
    }

    // Converts an object to an mxGraph conform style string
    public static styleObjectToString(styleObj: any) {
        let style = ""
        for (const key of Object.keys(styleObj)) {
            if (styleObj[key] === "removed") continue
            if (styleObj[key] !== "bool") {
                style += key + "=" + styleObj[key] + ";";
            } else {
                style += key + ";";
            }
        }
        return style
    }

    //returns a merged style where all states are applied based on the given state order
    public static getCellStyleForGlyphStates(cell: mxCell, baseState: GlyphState, states: GlyphState[]) {
        let style: any = {};
        for (const state of [baseState, ...states]) {
            const newStyle = state.changes.find(c => c.cellId === cell.id)?.style;
            if (!newStyle) continue
            Object.assign(style, newStyle);
            cell.setVisible(!newStyle.hide || newStyle.hide === "0");
        }
        return style;
    }

    public static getCellGeometryForGlyphStates(cell: mxCell, baseState: GlyphState, states: GlyphState[]) {
        const geometry = Object.assign({}, { ...baseState.changes.find(state => state.cellId === cell.id)?.geometry });
        if (!geometry) return {}
        const initialGeometry = Object.assign({}, geometry);
        let updatedPoints = false; // used to only update points of lines once
        for (let i = states.length - 1; i >= 0; i--) {
            const state = states[i];
            const stateGeometry = state.changes.find(c => c.cellId === cell.id)?.geometry;
            if (!stateGeometry) continue;

            // update the start end endpoints on lines
            if (geometry.sourcePoint && stateGeometry.sourcePoint) {
                const point = { ...geometry.sourcePoint };
                if (initialGeometry.sourcePoint.x === point.x) {
                    point.x += stateGeometry.sourcePoint.x;
                }
                if (initialGeometry.sourcePoint.y === point.y) {
                    point.y += stateGeometry.sourcePoint.y;
                }
                geometry.sourcePoint = Object.assign(new mx.mxPoint(), point);
            }
            if (geometry.targetPoint && stateGeometry.targetPoint) {
                const point = { ...geometry.targetPoint };
                if (initialGeometry.targetPoint.x === point.x) {
                    point.x += stateGeometry.targetPoint.x;
                }
                if (initialGeometry.targetPoint.y === point.y) {
                    point.y += stateGeometry.targetPoint.y;
                }
                geometry.targetPoint = Object.assign(new mx.mxPoint(), point);
            }

            // update the points of a line that define the lines curvature
            if (stateGeometry.points && !updatedPoints) {
                updatedPoints = true;
                if (!geometry.points) {
                    geometry.points = stateGeometry.points.map((point: mxPoint) => Object.assign(new mx.mxPoint(), point));
                } else {
                    geometry.points = stateGeometry.points.map((point: mxPoint, index: number) => {
                        if (index > geometry.points.length - 1) {
                            return Object.assign(new mx.mxPoint(), point)
                        }
                        const newPoint = { ...point }
                        if (geometry.points[index].x)
                            newPoint.x += geometry.points[index].x;
                        newPoint.y += geometry.points[index].y;
                        return Object.assign(new mx.mxPoint(), newPoint);
                    })
                }
            }

            // only apply geometry changes if a previous more important change hasn't already altered them
            if (initialGeometry.x === geometry.x) {
                geometry.x += stateGeometry.x || 0;
            }
            if (initialGeometry.y === geometry.y) {
                geometry.y += stateGeometry.y || 0;
            }
            if (initialGeometry.width === geometry.width) {
                geometry.width += stateGeometry.width || 0;
            }
            if (initialGeometry.height === geometry.height) {
                geometry.height += stateGeometry.height || 0;
            }
        }

        return Object.assign(cell.getGeometry(), geometry);
    }

    //mutates the base geometry
    public static addGeometries(base: mxGeometry, add: mxGeometry) {
        base.x += add.x || 0;
        base.y += add.y || 0;
        base.width += add.width || 0;
        base.height += add.height || 0;
        return base
    }

    public static getCellLabelForGlyphStates(cell: mxCell, baseState: GlyphState, states: GlyphState[]) {
        const mergedStates = [baseState, ...states]
        if (mergedStates.length <= 0) return
        mergedStates.reverse()
        for (const state of mergedStates) {
            const label = state.changes.find(change => change.cellId === cell.id)?.value;
            if (label !== undefined) return label
        }
        return ""
    }

    public static setVariableCellLabel(cell: mxCell, label: string | undefined | any) {
        if (label === undefined) {
            return;
        }
        if (cell.getValue() && typeof cell.getValue() === 'object' && cell.getValue().getAttribute) { // if value is an XML-node, change the node value
            cell.getValue().setAttribute("label", label);
        } else {
            cell.setValue(label);
        }
    }

    public static setCellTerminals(cell: mxCell, baseState: GlyphState, states: GlyphState[], model: mxGraphModel) {
        const mergedStates = [baseState, ...states]
        if (mergedStates.length <= 0) return
        mergedStates.reverse();
        let sourceFound = false;
        let targetFound = false;
        for (const state of mergedStates) {
            const source = state.changes.find(change => change.cellId === cell.id)?.source;
            const target = state.changes.find(change => change.cellId === cell.id)?.target;
            if (source && !sourceFound) {
                const sourceCell = model.getCell(source);
                sourceCell && cell.setTerminal(sourceCell, true);
                sourceFound = true;
            }
            if (target && !targetFound) {
                const targetCell = model.getCell(target);
                targetCell && cell.setTerminal(targetCell, false);
                targetFound = true;
            }
            if (targetFound && sourceFound) {
                return
            }
        }
    }

    //Returns a style object that only contains the key values that differ between one and another style string from mxGraph
    public static getStyleDifference(styleOld: string, styleNew: string) {
        const s1 = this.styleStringToObject(styleOld);
        const s2 = this.styleStringToObject(styleNew);
        for (const key of Object.keys(s1)) {
            if (s2[key] === s1[key]) {
                delete s2[key];
            } else if (s1[key] && !s2[key]) {
                s2[key] = "removed"
            }
        }
        return s2
    }

    //Returns an object that contains the difference between x,y,width,height of one and another mxGeometry.
    public static getGeometryDifference(defaultGeometry: any, newGeometry: any) {
        newGeometry = { ...newGeometry };
        if (!defaultGeometry) {
            return newGeometry;
        }
        for (const key of Object.keys(defaultGeometry)) {
            if (newGeometry[key] === defaultGeometry[key]) {
                delete newGeometry[key];
            }
        }
        if (newGeometry.x) {
            newGeometry.x -= defaultGeometry.x;
        } else {
            //newGeometry.x = 0;
        }
        if (newGeometry.y) {
            newGeometry.y -= defaultGeometry.y;
        } else {
            //newGeometry.y = 0;
        }
        if (newGeometry.width) {
            newGeometry.width -= defaultGeometry.width;
        } else {
            //newGeometry.width = 0;
        }
        if (newGeometry.height) {
            newGeometry.height -= defaultGeometry.height;
        } else {
            //newGeometry.height = 0;
        }

        if (defaultGeometry.sourcePoint && newGeometry.sourcePoint) {
            newGeometry.sourcePoint = this.getPointDifference(defaultGeometry.sourcePoint, newGeometry.sourcePoint);
        }
        if (defaultGeometry.targetPoint && newGeometry.targetPoint) {
            newGeometry.targetPoint = this.getPointDifference(defaultGeometry.targetPoint, newGeometry.targetPoint);
        }

        if (newGeometry.points) {
            newGeometry.points = this.getPointsDifference(defaultGeometry.points, newGeometry.points)
        }

        return newGeometry
    }

    public static getPointsDifference(defaultPoints: mxPoint[], newPoints: mxPoint[]) {
        if (!defaultPoints) {
            return newPoints
        }
        return newPoints.map((newPoint, index) => {
            if (index > defaultPoints.length - 1) {
                return newPoint
            }
            return this.getPointDifference(defaultPoints[index], newPoint)
        })
    }

    public static getPointDifference(defaultPoint: mxPoint, newPoint: mxPoint) {
        const difference = { ...newPoint };
        difference.x -= defaultPoint.x;
        difference.y -= defaultPoint.y;
        return difference;
    }

    public static cellIsVisibleInNoState(cellId: string, states: GlyphState[]) {
        for (const state of states) {
            const change = state.changes.find(c => c.cellId === cellId)?.style
            if (!change || (!change.hide || change.hide === "0")) {
                return false;
            }
        }
        return true;
    }

    public static getChildOfCellById(cell: mxCell, childId: string): any {
        if (cell.id === childId) return cell;
        if (!cell.children) return undefined;
        for (const child of cell.children) {
            const found = this.getChildOfCellById(child, childId);
            if (found) {
                return found;
            }
        }
        return undefined
    }

    public static getAllChildrenOfCell(cell: mxCell): mxCell[] {
        if (!cell) {
            return [];
        }
        let cells: mxCell[] = [cell];
        if (cell.children && cell.children.length > 0) {
            for (const c of cell.children) {
                cells = [...cells, ...this.getAllChildrenOfCell(c)];
            }
        }
        return cells;
    }

    public static unlinkCellFromIdeaboxEntry(cell: mxCell, entry: IdeaBoxEntry) {
        this.linkCellToIdeaBoxEntry(cell, entry, true);
    }

    public static linkCellToIdeaBoxEntry(cell: mxCell, entry: IdeaBoxEntry, remove?: boolean) {
        if (!entry) {
            return;
        }

        // Update cell entries
        if (!remove) {
            !entry.linkedCells.find((id: string) => cell.id === id) && entry.linkedCells.push(cell.id);
        } else {
            entry.linkedCells = entry.linkedCells.filter((id: string) => id !== cell.id);
        }
    }

    public static exportGraph(graph: mxGraph) {
        //var pageCount = mx.mxUtils.prompt('Enter page count', '1');
        let pageCount = 1;
        if (pageCount != null) {
            var scale = mx.mxUtils.getScaleForPageCount(pageCount, graph);
            var preview = new mx.mxPrintPreview(graph, scale);
            preview.title = "HMI Preview";
            preview.open();
            domtoimage.toBlob(preview.getWindow().document.getElementById("mxPage-1"))
                .then(function (blob) {
                    window.saveAs(blob, 'hmi.png');
                });
        }
    }

    public static isCellInvisible(cell: mxCell) {
        return !cell.visible
    }

    public static enableCell(cell: any) {
        this.disableCell(cell, true);
    }

    public static disableCell(cell: any, enable?: boolean) {
        if (!cell) return;
        cell.setConnectable(enable);
        let style = cell.getStyle() ? cell.getStyle() : "";
        if (enable) {
            style = style.replaceAll("locked=1;", "");
        } else {
            style += "locked=1;"
        }
        cell.setStyle(style);
        /** also locks children if needed
        if (!cell.children) return;
        for (const child of cell.children) {
            disableCell(child, enable);
        }
        */
    };

    // This function enables using labels with a graph
    public static addIdeaBoxEntryLabelSupport(graph: any, iframe: any, ideabox: IdeaBox) {
        graph.setExtendParents(true);
        graph.setExtendParentsOnAdd(true);
        graph.setExtendParentsOnMove(true);
        graph.setAllowNegativeCoordinates(true);

        //////////////// Show labels upon hovering

        var selection: mxCell[] = [];

        function getCellsOfIdeaBoxEntry(entry: IdeaBoxEntry) {
            if (!entry.linkedCells || entry.linkedCells.length <= 0) return []
            const model = graph.getModel();
            const cells = [];
            for (const id of entry.linkedCells) {
                const cell = model.getCell(id);
                cell && cells.push(cell);
            }
            return cells;
        }


        function drawLabels(labels: mxCell[]) {
            for (const cell of labels) {
                drawLabel(cell);
            }
            removeInactiveLabels(labels);
        };

        function getIdeaBoxEntriesOfCell(cell: mxCell) {
            const entries = []
            for (const entry of ideabox.entries) {
                if (entry.linkedCells.find(cellId => cellId === cell.id)) {
                    entries.push(entry);
                }
            }
            return entries;
        }

        graph.getSelectionModel().addListener(mx.mxEvent.CHANGE, (g: any) => {
            selection = [...g.cells];
            drawLabels(selection);
        });


        graph.addListener(mx.mxEvent.CELLS_MOVED, (sender: any, evt: any) => {
            setTimeout(() => { drawLabels(evt.properties.cells); }, 1)
        });


        graph.addListener(mx.mxEvent.CELLS_RESIZED, (sender: any, evt: any) => {
            setTimeout(() => { drawLabels(evt.properties.cells); }, 1)
        });

        graph.highlightCellsWithEntry = function (entry: IdeaBoxEntry, highlight: boolean) {
            const cells = getCellsOfIdeaBoxEntry(entry);
            if (cells.length <= 0) return;
            if (highlight) {
                drawLabels(cells);
            } else {
                drawLabels(selection);
            }
        }

        //Remove display of tooltips
        graph.getTooltipForCell = () => { };

        // Changes label visibility on hover
        let mouseDown = false;
        graph.addMouseListener({
            currentState: null,
            previousStyle: null,
            mouseDown: function (sender: any, me: any) { },
            mouseUp: function (sender: any, me: any) { mouseDown = false },
            dragEnter: function (evt: any, state: any) { },
            dragLeave: function (evt: any, state: any) { },
            mouseMove: function (sender: any, me: any) {
                if (mouseDown || (this.currentState != null && me.getState() === this.currentState)) {
                    return;
                }
                var tmp = graph.view.getState(me.getCell());

                if (graph.isMouseDown) {
                    mouseDown = true;
                    graph.removeAllLabels();
                    tmp = null;
                    return;
                }

                if (tmp !== this.currentState) {
                    this.currentState = tmp;

                    if (this.currentState != null) {
                        drawLabels([...selection, this.currentState.cell]);
                        GlyphEventService.dispatch(GlyphEvent.HIGHLIGHT_ENTRIES, getIdeaBoxEntriesOfCell(this.currentState.cell));
                    } else {
                        drawLabels(selection)
                        GlyphEventService.dispatch(GlyphEvent.HIGHLIGHT_ENTRIES, []);
                    }
                }
            }
        });

        // INIT SECOND LABEL

        // Hook for returning the label of the cell
        graph.getLabelForIdeaBoxEntries = function (entries: IdeaBoxEntry[]) {
            let cellLabels = '';
            for (const entry of entries) {
                const color = entry.type.includes(TaskType.INFORMATION) ? "#ffc107" : "#7edeea";
                const interfaceName = entry?.interfaceElement.split(".").reverse()[0];
                cellLabels += `<div id="glyph${nanoid()}" class="konect-label" style="background-color: ${color};">` +
                    `<span>${entry.task.name}</span>` +
                    `</br>` +
                    `<span style="font-weight: bold;">${interfaceName}</span>` +
                    `<div><span style="border-bottom-style: solid;border-width: 1px;border-color: #b8b8b8;display: block;">${entry.insight.name}</span>` +
                    `<span style="display: block;">${entry.selectedAttribute.name}</span>` +
                    `<span class="delete-button"><span class="geSprite geSprite-delete"/></span>` +
                    `</div>` +
                    `</div>`
            }

            let labels = `<div class="konect-label-container">
            <div style="display: inline-flex; pointer-events: all">
                ${cellLabels}
            </div>
            </div>`
            return labels;
        };

        var relativeChildVerticesVisible = true;

        // Overrides method to hide relative child vertices
        graph.isCellVisible = function (cell: mxCell) {
            return (!this.model.isVertex(cell) || cell.geometry === null ||
                !cell.geometry.relative ||
                cell.geometry.relative === relativeChildVerticesVisible) && cell.visible;
        };


        /** DEPRECATED use this if a second cell label should be reintroduced for whatever reason
        graph.cellRenderer.getShapesForState = function (state: any) {
            return [state.shape, state.text, state.secondLabel, state.control];
        };
         */

        graph.setCellOpacity = (cell: any, value: number) => {
            if (!cell) return;
            const cellState = graph.view.getState(cell);
            if (cellState && cellState.shape && cellState.shape.node) {
                const node = cellState.shape.node;
                node.setAttribute("opacity", value);
            }
            if (!cell.children) return;
            for (const child of cell.children) {
                graph.setCellOpacity(child, value);
            }
        }

        function removeInactiveLabels(currentSelection: mxCell[]) {
            const svg = graph.view.getDrawPane().ownerSVGElement;
            for (const foreign of [...Array.from(svg.children).filter((child: any) => child.tagName === "foreignObject")]) {
                //@ts-ignore
                if (currentSelection.find(cell => "label" + cell.id === foreign.id)) {
                    continue
                }
                //@ts-ignore
                foreign.remove();
            }
        }

        graph.removeAllLabels = () => {
            const svg = graph.view.getDrawPane().ownerSVGElement;
            for (const foreign of [...Array.from(svg.children).filter((child: any) => child.tagName === "foreignObject")]) {
                //@ts-ignore
                foreign.remove();
            }
        }

        function drawLabel(cell: mxCell) {
            if (!cell) return;
            const entries = getIdeaBoxEntriesOfCell(cell);
            if (entries.length <= 0) return;

            const svg = graph.view.getDrawPane().ownerSVGElement;
            let foreign: any = svg.getElementById("label" + cell.id);

            // if label is already drawn skip drawing it
            if (foreign) return;

            foreign = document.createElementNS('http://www.w3.org/2000/svg', "foreignObject");
            foreign.style.pointerEvents = "none";
            //@ts-ignore
            foreign.classname = "label-container-node";
            foreign.setAttribute('width', "10000px");
            foreign.setAttribute('height', "10000px");
            foreign.id = "label" + cell.id;
            svg.appendChild(foreign);

            foreign.innerHTML = graph.getLabelForIdeaBoxEntries(entries);

            //Add onclick to label to remove it.
            const buttons = foreign.getElementsByClassName('delete-button');
            entries.forEach((entry: IdeaBoxEntry, index: number) => {
                buttons[index].onclick = (e: any) => {
                    drawLabels([]);
                    graph.onRemoveLinkFromCell(cell, entry);
                }
            })

            const state = graph.view.getState(cell);
            if (!state) return;
            foreign.style.display = "block";
            foreign.setAttribute('x', (state.x + state.width / 2 - 5000));
            foreign.setAttribute('y', (state.y + state.height + 10 - 5000));

            // add drag and drop functionality to labels
            const subLabels = foreign.getElementsByClassName('konect-label');
            let index = 0;
            for (const l of subLabels) {
                const entry = entries[index];
                const handleMove = (event: any) => {
                    const rect = foreign.getBoundingClientRect();
                    l.style.position = "absolute";
                    l.style.left = "0";
                    l.style.top = "0";
                    l.style.opacity = "0.5 !important";
                    l.style.transform = "translate(" + (event.clientX - rect.x + 10) + "px," + (event.clientY - rect.y + 10 - 5000) + "px)";
                    l.style.pointerEvents = "none";
                    foreign.style.pointerEvents = "all";
                    foreign.style.cursor = "crosshair"
                    foreign.style.opacity = "0.5";
                }

                l.onmousedown = (event: any) => {
                    if (event.target.className.includes("delete")) return;
                    iframe.addEventListener("mousemove", handleMove);
                    handleMove(event)

                    const handleMouseUp = (event: any) => {
                        l.style.position = "relative";
                        l.style.transform = "translate(0px, 0px)";
                        l.style.pointerEvents = "all";
                        iframe.removeEventListener("mousemove", handleMove);
                        iframe.removeEventListener("mouseup", handleMouseUp);
                        foreign.style.pointerEvents = "none";
                        foreign.style.opacity = "1";
                        event.entry = entry;
                        graph.handleLabelDrop && graph.handleLabelDrop(event, cell);
                    }
                    iframe.addEventListener("mouseup", handleMouseUp);
                }
                index++;
            }
        }
    }
}

